import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_CABLE_DELIVERIES,
  COLLECTION_ID_DELIVERY_CONTRACT_QTY
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const LEGACY_CONTRACT_KEY = 'deliveryTracking:contractQty:v1';
const LEGACY_CABLE_UNIT_KEY = 'deliveryTracking:cableUnit:v1';
const CONTRACT_QTY_PO_PREFIX = '__contractQty__';

export const isContractQtyStoragePurchaseOrderId = (purchaseOrderId = '') =>
  String(purchaseOrderId).startsWith(CONTRACT_QTY_PO_PREFIX);

const contractQtyPurchaseOrderId = (storageScope) =>
  `${CONTRACT_QTY_PO_PREFIX}${storageScope}`;

const parseJsonObject = (raw) => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const parseContractPayload = (raw) => {
  const parsed = parseJsonObject(raw);
  if (parsed.kind === 'deliveryContractQty') {
    return {
      contractQtys: parseJsonObject(parsed.contractQtys),
      cableUnits: parseJsonObject(parsed.cableUnits)
    };
  }
  return {
    contractQtys: parseJsonObject(parsed.contractQtys),
    cableUnits: parseJsonObject(parsed.cableUnits)
  };
};

const readLegacyScopeMap = (storageKey, storageScope) => {
  if (typeof window === 'undefined' || !storageScope) return {};
  try {
    const all = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return all[storageScope] && typeof all[storageScope] === 'object' ? all[storageScope] : {};
  } catch {
    return {};
  }
};

const hasAnyKeys = (obj) => Object.keys(obj || {}).length > 0;

export const polesContractStorageScope = (lotTab) => `poles:${lotTab || ''}`;

export const materialContractStorageScope = (materialTab, lotTab) =>
  `${materialTab}:${lotTab || ''}`;

async function findDedicatedScopeDocument(storageScope) {
  const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_DELIVERY_CONTRACT_QTY, [
    Query.equal('storageScope', storageScope),
    Query.limit(1)
  ]);
  return response.documents[0] || null;
}

async function findCableSlotScopeDocument(storageScope) {
  const poId = contractQtyPurchaseOrderId(storageScope);
  const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_CABLE_DELIVERIES, [
    Query.equal('purchaseOrderId', poId),
    Query.limit(1)
  ]);
  return response.documents[0] || null;
}

function dedicatedDocToState(doc) {
  if (!doc) return { contractQtys: {}, cableUnits: {} };
  return {
    contractQtys: parseJsonObject(doc.contractQtys),
    cableUnits: parseJsonObject(doc.cableUnits)
  };
}

async function loadScopeStateFromBackend(storageScope) {
  try {
    const dedicated = await findDedicatedScopeDocument(storageScope);
    if (dedicated) return dedicatedDocToState(dedicated);
  } catch (err) {
    if (err?.code !== 404) {
      console.warn('DeliveryContractQtySGM unavailable, using Cable deliveries slot:', err?.message || err);
    }
  }

  const cableSlot = await findCableSlotScopeDocument(storageScope);
  if (!cableSlot) return { contractQtys: {}, cableUnits: {} };
  return parseContractPayload(cableSlot.deliveryState);
}

async function upsertDedicatedScopeState(storageScope, contractQtys, cableUnits) {
  const payload = {
    storageScope,
    contractQtys: JSON.stringify(contractQtys || {}),
    cableUnits: JSON.stringify(cableUnits || {})
  };

  const existing = await findDedicatedScopeDocument(storageScope);
  if (existing) {
    await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_DELIVERY_CONTRACT_QTY,
      existing.$id,
      payload
    );
    return;
  }

  await databases.createDocument(DATABASE_ID, COLLECTION_ID_DELIVERY_CONTRACT_QTY, ID.unique(), payload);
}

async function upsertCableSlotScopeState(storageScope, contractQtys, cableUnits) {
  const purchaseOrderId = contractQtyPurchaseOrderId(storageScope);
  const deliveryState = JSON.stringify({
    kind: 'deliveryContractQty',
    contractQtys: contractQtys || {},
    cableUnits: cableUnits || {}
  });

  const existing = await findCableSlotScopeDocument(storageScope);
  if (existing) {
    await databases.updateDocument(DATABASE_ID, COLLECTION_ID_CABLE_DELIVERIES, existing.$id, {
      deliveryState
    });
    return;
  }

  await databases.createDocument(DATABASE_ID, COLLECTION_ID_CABLE_DELIVERIES, ID.unique(), {
    purchaseOrderId,
    deliveryState
  });
}

async function upsertScopeState(storageScope, contractQtys, cableUnits) {
  try {
    await upsertDedicatedScopeState(storageScope, contractQtys, cableUnits);
    return;
  } catch (err) {
    console.warn('Saving to DeliveryContractQtySGM failed, using Cable deliveries slot:', err?.message || err);
  }
  await upsertCableSlotScopeState(storageScope, contractQtys, cableUnits);
}

/**
 * Load contract qty (+ cable units) for a scope. Migrates browser localStorage once if backend is empty.
 */
export async function fetchDeliveryContractScope(storageScope) {
  if (!storageScope) {
    return { contractQtys: {}, cableUnits: {} };
  }

  try {
    let state = await loadScopeStateFromBackend(storageScope);

    const legacyContract = readLegacyScopeMap(LEGACY_CONTRACT_KEY, storageScope);
    const legacyUnits = readLegacyScopeMap(LEGACY_CABLE_UNIT_KEY, storageScope);
    const backendEmpty =
      !hasAnyKeys(state.contractQtys) && !hasAnyKeys(state.cableUnits);
    const legacyHasData = hasAnyKeys(legacyContract) || hasAnyKeys(legacyUnits);

    if (backendEmpty && legacyHasData) {
      state = {
        contractQtys: { ...legacyContract },
        cableUnits: { ...legacyUnits }
      };
      await upsertScopeState(storageScope, state.contractQtys, state.cableUnits);
    }

    return state;
  } catch (err) {
    console.error('fetchDeliveryContractScope failed:', err);
    return {
      contractQtys: readLegacyScopeMap(LEGACY_CONTRACT_KEY, storageScope),
      cableUnits: readLegacyScopeMap(LEGACY_CABLE_UNIT_KEY, storageScope)
    };
  }
}

/** Browser backup when Appwrite is unavailable or before explicit Save. */
export function backupContractScopeToLocalStorage(storageScope, contractQtys, cableUnits = {}) {
  if (typeof window === 'undefined' || !storageScope) return;
  try {
    const allC = JSON.parse(window.localStorage.getItem(LEGACY_CONTRACT_KEY) || '{}');
    allC[storageScope] = { ...(contractQtys || {}) };
    window.localStorage.setItem(LEGACY_CONTRACT_KEY, JSON.stringify(allC));
    const allU = JSON.parse(window.localStorage.getItem(LEGACY_CABLE_UNIT_KEY) || '{}');
    allU[storageScope] = { ...(cableUnits || {}) };
    window.localStorage.setItem(LEGACY_CABLE_UNIT_KEY, JSON.stringify(allU));
  } catch {
    /* ignore */
  }
}

/** Save full contract qty map (+ cable units) for a scope. */
export async function saveDeliveryContractScopeState(
  storageScope,
  contractQtys,
  cableUnits = {}
) {
  if (!storageScope) return;
  await upsertScopeState(storageScope, contractQtys || {}, cableUnits || {});
  backupContractScopeToLocalStorage(storageScope, contractQtys, cableUnits);
}

export async function saveDeliveryContractQtyEntry(storageScope, itemKey, value) {
  if (!storageScope || !itemKey) return;
  const state = await loadScopeStateFromBackend(storageScope);
  const contractQtys = { ...state.contractQtys, [itemKey]: String(value ?? '') };
  await saveDeliveryContractScopeState(storageScope, contractQtys, state.cableUnits);
}

export async function saveDeliveryCableUnitEntry(storageScope, itemKey, value) {
  if (!storageScope || !itemKey) return;
  const state = await loadScopeStateFromBackend(storageScope);
  const cableUnits = { ...state.cableUnits, [itemKey]: String(value ?? '') };
  await saveDeliveryContractScopeState(storageScope, state.contractQtys, cableUnits);
}
